import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { FilterSetName } from 'store/modules/routerUtils/types';
import { EnterpriseStatus } from 'types/models/enterprise';
import { useApplyCachedSearchParams } from 'utils/Router/use-apply-cached-search-params';

const useApplyDefaultSearchParams = () => {
  const { pathname } = useLocation();
  const { applied } = useApplyCachedSearchParams(pathname);
  const [isSearchParamsReady, setIsSearchParamsReady] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!applied || isSearchParamsReady) {
      return;
    }

    // URL already has params (e.g. from cache), so we do not override.
    if (searchParams.size > 0) {
      setIsSearchParamsReady(true);
      return;
    }

    // Cached check has run and searchParams is still empty(*), so we apply defaultSearchParams now.
    // (*) Note that we accept one edge case: if user did clear filters before navigating away, cached check would run
    // and searchParams would still be empty; that "cleared state" will be overridden by defaultSearchParams when user
    // navigates back to the page.
    setSearchParams((oldSearchParams) => {
      const name = `filter.${FilterSetName.BACK_OFFICE_ENTERPRISES}.status[]`;
      if (oldSearchParams.has(name)) {
        return oldSearchParams;
      }

      const newSearchParams = new URLSearchParams(oldSearchParams);
      newSearchParams.append(name, EnterpriseStatus.ACTIVE);
      newSearchParams.append(name, EnterpriseStatus.DEMO);
      newSearchParams.append(name, EnterpriseStatus.PAST_DUE);
      newSearchParams.append(name, EnterpriseStatus.TEST);
      newSearchParams.append(name, EnterpriseStatus.TRIAL);
      newSearchParams.append(name, EnterpriseStatus.TRIAL_EXPIRED);
      return newSearchParams;
    });
    setIsSearchParamsReady(true);
  }, [applied, isSearchParamsReady, searchParams, setSearchParams]);

  return { isSearchParamsReady };
};

export default useApplyDefaultSearchParams;
